/**
 * useIdeaDetailContent
 *
 * Extracts the editable idea-detail UI (header, body, footer, side-effect
 * portals) from its chrome so the same content can mount inside either the
 * admin ``Modal`` or the creator ``Drawer``. Behaviour is unchanged from
 * the previous inline implementation in ``IdeaDetailModal``.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { IdeaDetailBody } from "./IdeaDetailBody";
import { IdeaStatusBadge } from "./IdeaStatusBadge";
import { IdeaTasksSection } from "@/domains/Tasks/components/IdeaTasksSection";
import type { TaskNavOptions } from "@/domains/Tasks/drawer/TaskDrawer";
import { ResourceEmbedCard } from "@/components/ui/ResourceEmbedCard";
import { useAccountRealtimeSync } from "@/hooks/useAccountRealtimeSync";
import {
  FolderIcon,
  DocumentIcon,
  PlusIcon,
  LinkIcon,
  CollectionIcon,
  UploadIcon,
  TrashIcon,
  SpinnerIcon,
  CloseIcon,
  StarIcon,
} from "@/components/icons";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import {
  IdeaStatus,
  IdeaContentType,
  IdeaResource,
  IdeaBody,
  IdeaBodySectionKey,
  ideaKeys,
} from "../types";
import { Post } from "@/core/types/postTypes";
import {
  useIdeaQuery,
  useUpdateIdeaMutation,
  useDeleteIdeaMutation,
} from "../hooks/useIdeas";
import { usePostModals } from "@/domains/PostCreator/hooks/usePostModals";
import { usePostsQuery } from "@/domains/PostCreator/hooks/usePostsQuery";
import { MinimalPostCard } from "@/domains/PostCreator/cards/MinimalPostCard";
import postsService from "@/domains/PostCreator/services/postsService";
import { ideasService } from "../services/ideasService";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { UploadMediaModal } from "@/domains/LibraryView/components/UploadMediaModal";
import { useIdeaCollection } from "../hooks/useIdeaCollection";
import { useEntityLinks } from "@/domains/Home/hooks/useEntityLinks";
import * as collectionsService from "@/services/collectionsService";
import type { Collection } from "@/services/collectionsService";
import { RoleProvider } from "@/contexts/RoleContext";
import { UserRole } from "@/services/urlService";

import { IDEA_CONTENT_TYPE_OPTIONS } from "../ideaContentTypeConfig";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import type { DropdownOption } from "@/components/ui/OptionDropdown";

// "" clears the content type, mirroring the old select's empty option.
const CONTENT_TYPE_PICKER_OPTIONS: DropdownOption<IdeaContentType | "">[] = [
  { value: "", label: "Type…" },
  ...IDEA_CONTENT_TYPE_OPTIONS,
];

export interface UseIdeaDetailContentOptions {
  ideaId: string | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  /**
   * When true, suppress admin-only internal-planning UI (entity-link chips,
   * linked notes, attached posts, collection / upload / link-to-post
   * controls). External ``idea.resources`` URLs stay visible and editable.
   */
  hideInternalResources?: boolean;
  /** The page's breadcrumb options, for links followed from the task drawer. */
  trailNavOptions?: TaskNavOptions;
  /**
   * Where the action buttons (Upload media, New Post) live. "header" (default,
   * modal/drawer) bundles them into the header action row. "panels" (the SMM
   * workspace page) drops the header action row and exposes the actions via
   * ``mediaActions`` / ``createPostAction`` so the page can place each in the
   * panel it belongs to.
   */
  actionsPlacement?: "header" | "panels";
  /** Accepted from IdeaDetailDrawer's shared props; consumed by the drawer
   * chrome, not this hook. */
  footerExtra?: React.ReactNode;
}

export interface IdeaDetailContent {
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
  /** Out-of-tree portals (upload modal) that the chrome must render as siblings. */
  sideEffects: React.ReactNode;
  /** Upload-media action node, for the Media panel header (page/"panels" mode). */
  mediaActions: React.ReactNode;
  /** New Post action node, for the Posts panel header (page/"panels" mode). */
  createPostAction: React.ReactNode;
  isLoading: boolean;
  error: unknown;
  hasIdea: boolean;
}

export function useIdeaDetailContent(
  options: UseIdeaDetailContentOptions,
): IdeaDetailContent {
  const { ideaId, clientId, isOpen, onClose, onDelete, hideInternalResources = false, actionsPlacement = "header", trailNavOptions } = options;

  const [newResourceUrl, setNewResourceUrl] = useState("");
  const [isAddingResource, setIsAddingResource] = useState(false);
  const [isRemovingResource, setIsRemovingResource] = useState(false);
  const [isUnlinkingPost, setIsUnlinkingPost] = useState<string | null>(null);
  const [showAddResourceForm, setShowAddResourceForm] = useState(false);
  const [showIdeaUpload, setShowIdeaUpload] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [availableCollections, setAvailableCollections] = useState<Collection[]>([]);
  const [isLinkingCollection, setIsLinkingCollection] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const http = useHttpClient();
  // Gate admin-only fetches when the Creator view is active. The hooks
  // below hit /ideas/<id>/collection and /client/<id>/posts via the admin
  // apiClient and 401 under the portal JWT.
  const {
    collectionId: ideaCollectionId,
    isCreating: isCreatingCollection,
    ensureCollection: ensureIdeaCollection,
    refresh: refreshIdeaCollection,
  } = useIdeaCollection(hideInternalResources ? null : ideaId, clientId);
  const { createLink, deleteLink } = useEntityLinks(() => {
    queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    if (ideaId) queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
  });
  const { data: idea, isLoading, error } = useIdeaQuery(ideaId ?? undefined, true, true, true);
  const { data: allPosts = [] } = usePostsQuery(
    hideInternalResources ? undefined : (clientId || undefined)
  );
  const updateMutation = useUpdateIdeaMutation(clientId);
  const deleteMutation = useDeleteIdeaMutation(clientId);
  const { openCreateModalForIdea } = usePostModals();

  const linkedPosts = useMemo((): Post[] => {
    if (!idea?.posts || idea.posts.length === 0) return [];
    const linkedPostIds = new Set(idea.posts.map(p => p.id));
    return allPosts
      .filter(post => linkedPostIds.has(post.id))
      .map(postItem => postItem.backendData as unknown as Post);
  }, [idea?.posts, allPosts]);

  useEffect(() => {
    if (!isOpen) {
      setNewResourceUrl("");
      setShowAddResourceForm(false);
      setEditingTitle(null);
    }
  }, [isOpen]);

  // Refetch the idea when the resource-media-importer job completes for
  // the resource we're currently viewing. The modal swaps the Path B
  // blockquote for the InlineMediaPlayer on the next render.
  const { on } = useAccountRealtimeSync();
  useEffect(() => {
    if (!ideaId || !isOpen) return;
    return on("resource_media_imported", (payload) => {
      if (payload && payload.ideaId === ideaId) {
        queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
      }
    });
  }, [ideaId, isOpen, on, queryClient]);

  const canDelete = idea?.status === "DRAFT";

  const handleSectionSave = useCallback(
    async (key: IdeaBodySectionKey, value: string) => {
      if (!ideaId || !idea) return;
      const updatedBody: IdeaBody = { ...idea.body, [key]: value };
      await updateMutation.mutateAsync({ ideaId, data: { body: updatedBody } });
    },
    [ideaId, idea, updateMutation]
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      if (!ideaId) throw new Error("No idea ID");
      return ideasService.uploadImage(http, ideaId, file);
    },
    [http, ideaId]
  );

  const handleTitleSave = useCallback(async () => {
    if (!ideaId || editingTitle === null) return;
    const trimmed = editingTitle.trim();
    if (trimmed && trimmed !== idea?.title) {
      await updateMutation.mutateAsync({ ideaId, data: { title: trimmed } });
    }
    setEditingTitle(null);
  }, [ideaId, editingTitle, idea?.title, updateMutation]);

  const handleStatusChange = async (newStatus: IdeaStatus) => {
    if (!ideaId) return;
    await updateMutation.mutateAsync({ ideaId, data: { status: newStatus } });
  };

  const handleContentTypeChange = async (value: string) => {
    if (!ideaId) return;
    const contentType = value as IdeaContentType || undefined;
    await updateMutation.mutateAsync({ ideaId, data: { contentType } });
  };

  const handleAddResource = async () => {
    if (!newResourceUrl.trim() || !ideaId || !idea) return;
    const newResource: IdeaResource = { url: newResourceUrl.trim() };
    setIsAddingResource(true);
    try {
      await updateMutation.mutateAsync({
        ideaId,
        data: { resources: [...idea.resources, newResource] },
      });
      setNewResourceUrl("");
      setShowAddResourceForm(false);
    } finally {
      setIsAddingResource(false);
    }
  };

  const handleRemoveResource = async (index: number) => {
    if (!ideaId || !idea) return;
    setIsRemovingResource(true);
    try {
      await updateMutation.mutateAsync({
        ideaId,
        data: { resources: idea.resources.filter((_, i) => i !== index) },
      });
    } finally {
      setIsRemovingResource(false);
    }
  };

  const handleCreatePost = () => {
    if (!ideaId || !clientId) return;
    openCreateModalForIdea(ideaId, clientId);
  };

  const handleUnlinkPost = async (postId: string) => {
    if (!clientId || !ideaId) return;
    setIsUnlinkingPost(postId);
    try {
      await postsService.unlinkFromIdea(clientId, postId);
      queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
    } catch (err) {
      console.error("Failed to unlink post from idea:", err);
    } finally {
      setIsUnlinkingPost(null);
    }
  };

  const handleDelete = async () => {
    if (!ideaId) return;
    await deleteMutation.mutateAsync(ideaId);
    onDelete?.();
    onClose();
  };

  const collectionLink = idea?.links?.find(l => l.targetType === "collection");
  const postLink = idea?.links?.find(l => l.targetType === "post");

  const entityLinkChips = !hideInternalResources ? (
    <>
      {collectionLink && (
        <span className="inline-flex items-center gap-1 group/chip">
          <button
            onClick={() => {
              onClose();
              navigate(`../library/photos?collection=${collectionLink.targetId}`);
            }}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            <FolderIcon className="w-3.5 h-3.5" />
            <span className="truncate max-w-[160px]">
              {collectionLink.targetName || "Related Collection"}
            </span>
          </button>
          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await deleteLink(collectionLink.id);
                refreshIdeaCollection();
              } catch (err) {
                console.error("Failed to unlink:", err);
              }
            }}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-400 opacity-0 group-hover/chip:opacity-100 hover:text-rose-600 hover:bg-rose-50 transition-all"
            title="Unlink collection"
            aria-label="Unlink collection"
          >
            <CloseIcon className="w-3 h-3" />
          </button>
        </span>
      )}

      {postLink && (
        <button className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors">
          <DocumentIcon className="w-3.5 h-3.5" />
          View Original Post
        </button>
      )}

      {idea?.links && idea.links.filter(l => l.targetType !== "collection" && l.targetType !== "post").map((link) => {
        const typeLabels: Record<string, string> = {
          calendar_event: "Calendar",
          note: "Note",
        };
        const typeLabel = typeLabels[link.targetType] || link.targetType;
        const label = link.targetName || typeLabel;

        const handleUnlink = async (e: React.MouseEvent) => {
          e.stopPropagation();
          try {
            await deleteLink(link.id);
            refreshIdeaCollection();
          } catch (err) {
            console.error("Failed to unlink:", err);
          }
        };

        return (
          <span
            key={link.id}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md group/link"
          >
            <span className="truncate max-w-[160px]">{label}</span>
            <button
              onClick={handleUnlink}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 opacity-0 group-hover/link:opacity-100 hover:text-rose-600 hover:bg-rose-50 transition-all"
              title="Unlink"
              aria-label="Unlink"
            >
              <CloseIcon className="w-2.5 h-2.5" />
            </button>
          </span>
        );
      })}
    </>
  ) : null;

  const hasAnyEntityLink =
    !hideInternalResources &&
    (collectionLink || postLink || (idea?.links?.some(l => l.targetType !== "collection" && l.targetType !== "post") ?? false));

  const deleteButton = canDelete ? (
    <button
      onClick={handleDelete}
      disabled={deleteMutation.isPending}
      title="Delete idea"
      aria-label="Delete idea"
      className={`inline-flex items-center justify-center ${
        actionsPlacement === "panels" ? "w-7 h-7" : "w-9 h-9"
      } text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50`}
    >
      {deleteMutation.isPending ? (
        <SpinnerIcon className="w-4 h-4 animate-spin" />
      ) : (
        <TrashIcon className="w-4 h-4" />
      )}
    </button>
  ) : null;

  const header = idea ? (
    <div className="space-y-3">
      {/* HEADER ROW — header mode (modal/drawer): title left, property strip
          right-aligned so the header fills its width. In "panels" mode the
          block sits in the narrow Concept subheader, so the title takes the
          full row and the property strip stacks underneath; no bottom
          hairline — panels are flat (DD-010, DD-011). */}
      <div
        className={
          actionsPlacement === "header"
            ? "flex flex-col lg:flex-row lg:items-end lg:justify-between gap-x-8 gap-y-3 pb-3 border-b border-gray-200"
            : "flex flex-col gap-y-3"
        }
      >
        <div className="min-w-0 flex-1">
        {editingTitle !== null ? (
          <input
            ref={titleInputRef}
            type="text"
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleTitleSave(); }
              if (e.key === "Escape") setEditingTitle(null);
            }}
            className="text-xl lg:text-2xl font-bold tracking-tight text-gray-900 border-b-2 border-blue-500 focus:outline-hidden w-full bg-transparent leading-tight"
            placeholder="Idea title"
            autoFocus
          />
        ) : (
          <h2
            className="text-xl lg:text-2xl font-bold tracking-tight text-gray-900 cursor-text hover:text-gray-700 transition-colors leading-tight"
            onClick={() => setEditingTitle(idea.title)}
          >
            {idea.title}
          </h2>
        )}
        </div>

        {/* PROPERTY STRIP — compact inline row (status · type · date), right
            aligned beside the title. No tall stacked labels, so it costs one
            line, not a sixth of a small screen. Wraps below the title when
            narrow. */}
        <dl
          className={`flex flex-wrap items-center gap-x-3 gap-y-2 shrink-0 ${
            actionsPlacement === "header" ? "lg:justify-end" : ""
          }`}
        >
          <dd>
            <IdeaStatusBadge
              status={idea.status}
              onStatusChange={handleStatusChange}
              disabled={updateMutation.isPending}
            />
          </dd>
          <dd>
            <OptionDropdown
              value={idea.contentType ?? ""}
              options={CONTENT_TYPE_PICKER_OPTIONS}
              onChange={handleContentTypeChange}
              disabled={updateMutation.isPending}
            />
          </dd>
          <dd className="text-xs font-medium text-gray-400 whitespace-nowrap">
            {new Date(idea.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </dd>
          {updateMutation.isPending && (
            <dd className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
              Saving…
            </dd>
          )}
          {/* Panels mode has no action row below, so the delete control
              joins the property strip instead of opening a line of its own. */}
          {actionsPlacement === "panels" && deleteButton && <dd>{deleteButton}</dd>}
        </dl>
      </div>

      {/* In "panels" mode the linked collection is represented by the Media
          panel and linked posts by the Posts panel, so the header drops this
          redundant "Linked to" block; the modal/drawer (header mode) keep it. */}
      {actionsPlacement === "header" && hasAnyEntityLink && (
        <div>
          <dt className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-1.5">
            Linked to
          </dt>
          <dd className="flex flex-wrap items-center gap-2">
            {entityLinkChips}
          </dd>
        </div>
      )}

      {/* ACTION ROW — secondary cluster grouped as a segmented control
          on the left, primary CTA pushed right, destructive isolated.
          flex-wrap so cluster and CTA stack on mobile/drawer widths.
          In "panels" mode (the SMM page) the actions live in their own
          panels and delete sits in the property strip, so the whole row
          is suppressed there. */}
      {actionsPlacement === "header" && (
      <div className="flex flex-wrap items-center gap-2">
        {!hideInternalResources && (
          <>
            <div className="relative">
              <div className="inline-flex items-stretch bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden divide-x divide-gray-200">
                {!ideaCollectionId && ideaId && (
                  <>
                    <button
                      onClick={async () => {
                        const collections = await collectionsService.getCollections(clientId);
                        setAvailableCollections(collections);
                        setShowCollectionPicker(true);
                      }}
                      title="Link to an existing collection"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      Link
                    </button>
                    <button
                      onClick={() => ensureIdeaCollection()}
                      disabled={isCreatingCollection}
                      title={`Create a new ${ENTITY_CONFIG.collection.label.toLowerCase()}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCreatingCollection ? (
                        <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CollectionIcon className="w-3.5 h-3.5" />
                      )}
                      {ENTITY_CONFIG.collection.label}
                    </button>
                  </>
                )}
                <button
                  onClick={async () => {
                    const colId = await ensureIdeaCollection();
                    if (colId) setShowIdeaUpload(true);
                  }}
                  disabled={isCreatingCollection}
                  title="Upload media to this idea"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UploadIcon className="w-3.5 h-3.5" />
                  Upload
                </button>
              </div>

              {showCollectionPicker && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]">
                      Select a collection
                    </p>
                  </div>
                  {availableCollections.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-gray-400 text-center">
                      No collections available
                    </p>
                  ) : (
                    availableCollections.map((col) => (
                      <button
                        key={col.id}
                        onClick={async () => {
                          if (!ideaId) return;
                          setIsLinkingCollection(true);
                          try {
                            await createLink({
                              sourceType: "idea",
                              sourceId: ideaId,
                              targetType: "collection",
                              targetId: col.id,
                            });
                            refreshIdeaCollection();
                            setShowCollectionPicker(false);
                          } catch (err) {
                            console.error("Failed to link collection:", err);
                          } finally {
                            setIsLinkingCollection(false);
                          }
                        }}
                        disabled={isLinkingCollection}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-50 last:border-0 disabled:opacity-50"
                      >
                        <span className="truncate flex items-center gap-2">
                          <CollectionIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          {col.name || "Unnamed Collection"}
                        </span>
                        <span className="text-xs text-gray-400 shrink-0 ml-2">
                          {col.itemCount} items
                        </span>
                      </button>
                    ))
                  )}
                  <div className="p-2 border-t border-gray-100">
                    <button
                      onClick={() => setShowCollectionPicker(false)}
                      className="w-full text-center text-xs text-gray-500 hover:text-gray-700 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {linkedPosts.map((post) => (
              <div key={post.id} className="relative group w-9 h-9">
                <MinimalPostCard post={post} />
                <button
                  onClick={() => handleUnlinkPost(post.id)}
                  disabled={isUnlinkingPost === post.id}
                  className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 bg-rose-500 text-white rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-600 disabled:opacity-50 z-10"
                  title="Unlink post"
                  aria-label="Unlink post"
                >
                  {isUnlinkingPost === post.id ? (
                    <SpinnerIcon className="w-2.5 h-2.5 animate-spin" />
                  ) : (
                    <CloseIcon className="w-2.5 h-2.5" />
                  )}
                </button>
              </div>
            ))}

            {/* Spacer pushes the primary CTA to the right when there's
                room; collapses to zero when the row wraps so the CTA
                sits cleanly under the secondary cluster on mobile. */}
            <div className="grow basis-0" />

            <button
              onClick={handleCreatePost}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm hover:shadow ring-1 ring-blue-700/10 transition-all"
            >
              <PlusIcon className="w-4 h-4" />
              {ENTITY_CONFIG.post.createLabel}
            </button>
          </>
        )}

        {deleteButton && (
          <>
            <span className="w-px h-8 bg-gray-200" aria-hidden />
            {deleteButton}
          </>
        )}
      </div>
      )}

      {/* Platforms + Categories — header mode (modal/drawer) only. The page
          renders these on the breadcrumb row to reclaim vertical space. */}
      {actionsPlacement === "header" && ((idea.platforms && idea.platforms.length > 0) || (idea.contentCategories && idea.contentCategories.length > 0)) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {idea.platforms?.map((platform) => (
            <span key={platform} className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full">
              {platform}
            </span>
          ))}
          {idea.contentCategories?.map((cat) => (
            <span key={cat} className="px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded-full">
              {cat}
            </span>
          ))}
        </div>
      )}

      {/* Client feedback (if rejected) */}
      {idea.status === "REJECTED" && idea.clientFeedback && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm">
          <span className="font-medium text-rose-700">Feedback: </span>
          <span className="text-rose-600">{idea.clientFeedback}</span>
        </div>
      )}

      {/* Client review surface (independent of status) — stars + comment
          left by the client on the portal. Status lifecycle is separate
          from the review; both can coexist. */}
      {(typeof idea.clientRating === "number" || idea.clientComment) && (
        <div className="p-3 bg-amber-50/60 border border-amber-100 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-[0.1em]">
              Client review
            </span>
            {typeof idea.clientRating === "number" && (
              <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon
                    key={star}
                    className={`w-3.5 h-3.5 ${
                      star <= (idea.clientRating ?? 0)
                        ? "text-amber-500"
                        : "text-amber-200"
                    }`}
                    filled={star <= (idea.clientRating ?? 0)}
                  />
                ))}
              </span>
            )}
            {idea.clientReviewedAt && (
              <span className="text-xs text-amber-600/70">
                {new Date(idea.clientReviewedAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </div>
          {idea.clientComment && (
            <p className="mt-1.5 text-sm text-amber-900 leading-relaxed">
              “{idea.clientComment}”
            </p>
          )}
        </div>
      )}

      {/* Linked notes — internal planning chips. Header (modal/drawer) mode
          only; on the page workspace they clutter the top bar. */}
      {actionsPlacement === "header" && !hideInternalResources && idea.notes && idea.notes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {idea.notes.map((note) => (
            <span key={note.id} className="px-2 py-1 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md truncate max-w-[200px]" title={note.content}>
              {note.content}
            </span>
          ))}
        </div>
      )}
    </div>
  ) : (
    <h2 className="text-lg font-semibold text-gray-900">Loading...</h2>
  );

  // Footer slot is intentionally null: the toolbar that used to live
  // here (Link / Collection / Upload / + New Post / Delete) was hoisted
  // into the header so the modal reads as one "issue page" with all
  // properties and actions at the top. The resource attach UI (add link
  // + resource pills) lives in the body next to the embed it controls.
  const footer = null;

  const body = isLoading ? (
    <div className="flex items-center justify-center h-64">
      <div className="text-gray-500">Loading...</div>
    </div>
  ) : error ? (
    <div className="flex items-center justify-center h-64">
      <div className="text-red-500">Error loading idea</div>
    </div>
  ) : idea ? (
    // Padding is owned by the chrome (Modal `bodyPadding` / Drawer
    // body wrapper) so header and body share one consistent inset.
    // IdeaDetailBody owns the DD-013 order; the editable sources row
    // and admin tasks checklist ride in as slots.
    <IdeaDetailBody
      resources={idea.resources}
      body={idea.body || {}}
      onSectionSave={handleSectionSave}
      uploadFile={handleUploadFile}
      sourcesSlot={
        // Sources — labeled the same way as the header's property
        // fields ("Status", "Type", "Created", "Linked to") so the
        // relationship to the embed above and the body sections below
        // is read as: this is another property of the idea.
        <div>
        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
          Sources
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showAddResourceForm ? (
          <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-xs pl-3 pr-1 py-1 focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-400 transition-shadow">
            <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="url"
              value={newResourceUrl}
              onChange={(e) => setNewResourceUrl(e.target.value)}
              placeholder="Paste a URL…"
              className="bg-transparent border-0 text-sm w-64 px-2 py-1 focus:outline-hidden placeholder:text-gray-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleAddResource(); }
                if (e.key === "Escape") { setShowAddResourceForm(false); setNewResourceUrl(""); }
              }}
            />
            <button
              type="button"
              onClick={handleAddResource}
              disabled={!newResourceUrl.trim() || isAddingResource}
              className="inline-flex items-center justify-center min-w-[44px] px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAddingResource ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : "Add"}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddResourceForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 bg-white border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 rounded-lg transition-all"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add link
          </button>
        )}

        {idea.resources.map((resource, index) => (
          <ResourceEmbedCard
            key={index}
            resource={resource}
            variant="dense"
            onRemove={() => handleRemoveResource(index)}
            isRemoving={isRemovingResource}
          />
        ))}
        </div>
      </div>
      }
      tasksSlot={
        !hideInternalResources ? (
          <IdeaTasksSection ideaId={idea.id} clientId={clientId} navOptions={trailNavOptions} />
        ) : undefined
      }
    />
  ) : null;

  const sideEffects =
    !hideInternalResources && ideaCollectionId ? (
      <RoleProvider role={UserRole.ADMIN} clientId={clientId}>
        <UploadMediaModal
          isOpen={showIdeaUpload}
          onClose={() => setShowIdeaUpload(false)}
          clientId={clientId}
          contentType="auto"
          collectionId={ideaCollectionId}
          onUploadComplete={() => setShowIdeaUpload(false)}
        />
      </RoleProvider>
    ) : null;

  // Panel-mode action nodes — the page places these in the Media / Posts panel
  // headers instead of the bundled header action row (which "panels" mode hides).
  const mediaActions =
    !hideInternalResources && idea ? (
      <button
        onClick={async () => {
          const colId = await ensureIdeaCollection();
          if (colId) setShowIdeaUpload(true);
        }}
        disabled={isCreatingCollection}
        title="Upload media to this idea"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg shadow-xs hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 transition-colors"
      >
        {isCreatingCollection ? (
          <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <UploadIcon className="w-3.5 h-3.5" />
        )}
        Upload
      </button>
    ) : null;

  const createPostAction =
    !hideInternalResources && idea ? (
      <button
        onClick={handleCreatePost}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        {ENTITY_CONFIG.post.createLabel}
      </button>
    ) : null;

  return {
    header,
    body,
    footer,
    sideEffects,
    mediaActions,
    createPostAction,
    isLoading,
    error,
    hasIdea: Boolean(idea),
  };
}
